import { useEffect, useCallback, useState, type ReactElement } from 'react';
import { UnsavedChangesDialog } from '@/components/dialogs/unsaved-changes-dialog';

/**
 * useUnsavedChanges Hook
 *
 * Hook para detectar cambios no guardados en formularios y mostrar confirmación
 * antes de cerrar el dialog o la ventana del navegador.
 *
 * Previene pérdida accidental de datos con dos mecanismos:
 * 1. beforeunload listener - previene cierre de pestaña/navegación
 * 2. Dialog de confirmación personalizado - mejor UX que window.confirm
 *
 * @example
 * ```tsx
 * const form = useForm();
 * const { handleClose, UnsavedChangesDialog } = useUnsavedChanges({
 *   isDirty: form.formState.isDirty,
 *   onClose: () => setDialogOpen(false)
 * });
 *
* return (
*   <Dialog onOpenChange={(open) => !open && handleClose()}>
*     <YourFormFields />
*     {UnsavedChangesDialog}
*   </Dialog>
* );
 * ```
 */

export interface UseUnsavedChangesOptions {
  /** Si el formulario tiene cambios no guardados (ej: form.formState.isDirty) */
  isDirty: boolean;
  /** Callback a ejecutar cuando el usuario confirma cerrar */
  onClose: () => void;
  /** Mensaje personalizado (opcional) */
  message?: string;
  /** Si debe usar el dialog personalizado en lugar de window.confirm */
  useCustomDialog?: boolean;
}

export interface UseUnsavedChangesReturn {
  /** Función wrapper para cerrar que valida cambios no guardados */
  handleClose: () => void;
  /** Si hay cambios no guardados */
  hasUnsavedChanges: boolean;
  /** Componente del dialog de confirmación (renderizar en el JSX) */
  UnsavedChangesDialog: ReactElement;
}

export function useUnsavedChanges({
  isDirty,
  onClose,
  message = '¿Estás seguro de cerrar? Los cambios no guardados se perderán.',
  useCustomDialog = true
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Prevenir cierre de pestaña/navegación del navegador
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        // Chrome requiere returnValue para mostrar el diálogo
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, message]);

  // Manejar cierre del dialog
  const handleClose = useCallback(() => {
    if (isDirty) {
      if (useCustomDialog) {
        // Mostrar dialog personalizado
        setShowConfirmDialog(true);
      } else {
        // Usar window.confirm nativo
        const confirmed = window.confirm(message);
        if (confirmed) {
          onClose();
        }
      }
    } else {
      // No hay cambios, cerrar directamente
      onClose();
    }
  }, [isDirty, message, onClose, useCustomDialog]);

  // Confirmar cierre desde el dialog personalizado
  const confirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    onClose();
  }, [onClose]);

  // Cancelar cierre
  const cancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // Componente del dialog de confirmación
  const DialogComponent = (
    <UnsavedChangesDialog
      open={showConfirmDialog}
      onOpenChange={setShowConfirmDialog}
      onConfirm={confirmClose}
      onCancel={cancelClose}
      message={message}
    />
  );

  return {
    handleClose,
    hasUnsavedChanges: isDirty,
    UnsavedChangesDialog: DialogComponent
  };
}
